import { createContext, type ReactNode, useContext, useState } from "react";
import type { MapGeoJSONFeature } from "maplibre-gl";
import type { TLayerId } from "@/models/saferoads/interfaces";


type FeatureSelectionContext = {
	selectedFeatures: MapGeoJSONFeature[];
	layerIdMap: Record<string, TLayerId>;
	currentSelectedFeatureIndex: number;
	setSelectedFeatures: (features: MapGeoJSONFeature[]) => void;
	clearSelectedFeatures: () => void;
	setCurrentSelectedFeatureIndex: (i: number) => void;
	setLayerIdMap: (layerIdMap: Record<string, TLayerId>) => void;
	incrementSelectedFeature: () => void;
	decrementSelectedFeature: () => void;
};

const FeatureSelectionContext = createContext<FeatureSelectionContext | undefined>(undefined);

export function FeatureSelectionProvider({ children }: { children: ReactNode }) {
	const [selectedFeatures, setSelectedFeatures] = useState<MapGeoJSONFeature[]>([]);
	const [layerIdMap, setLayerIdMap] = useState<Record<string, TLayerId>>({});
	const [currentSelectedFeatureIndex, setCurrentSelectedFeatureIndex] = useState(0);

	const incrementSelectedFeature = () => {
		if (currentSelectedFeatureIndex + 1 < selectedFeatures.length) {
			setCurrentSelectedFeatureIndex(currentSelectedFeatureIndex + 1);
		} else {
			setCurrentSelectedFeatureIndex(0);
		}
	};

	const decrementSelectedFeature = () => {
		if (currentSelectedFeatureIndex > 0) {
			setCurrentSelectedFeatureIndex(currentSelectedFeatureIndex - 1);
		} else {
			setCurrentSelectedFeatureIndex(selectedFeatures.length - 1);
		}
	};

	const clearSelectedFeatures = () => setSelectedFeatures([]);

	const contextValue = {
		selectedFeatures,
		setSelectedFeatures,
		clearSelectedFeatures,
		currentSelectedFeatureIndex,
		setCurrentSelectedFeatureIndex,
		layerIdMap,
		setLayerIdMap,
		incrementSelectedFeature,
		decrementSelectedFeature,
	};

	return <FeatureSelectionContext.Provider value={contextValue}>{children}</FeatureSelectionContext.Provider>;
}

export const useFeatureSelection = () => {
	const context = useContext(FeatureSelectionContext);
	if (context === undefined) {
		throw new Error("useFeatureSelection must be used within a FeatureSelectionProvider");
	}
	return context;
};
